import React from 'react';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';
import MDVIPCallToAction from '../components/MDVIPCallToAction';
import AnimatedPage from '../components/AnimatedPage';

const OurPractice = () => {
  return (
    <AnimatedPage className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-stone-50 via-stone-50 to-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6">
              Our Practice
            </h1>
            <div className="h-1 w-24 bg-gradient-to-r from-amber-500 via-amber-500 to-orange-500 mx-auto mb-12"></div>
            
            {/* Practice Image - Moved Here */}
            <div className="max-w-5xl mx-auto mb-12">
              <div className="rounded-2xl overflow-hidden shadow-2xl hover-lift transition-all duration-300 mb-8">
                <img
                  src="https://customer-assets.emergentagent.com/job_premium-med-care/artifacts/apzzmprx_CFP%20LOGO%201.png"
                  alt="Crofton Family Practice Interior Logo Wall"
                  className="w-full h-auto object-cover"
                />
              </div>
              <p className="text-xl text-stone-800 font-semibold mb-8">
                1684 Village Green Lower Level, Crofton, MD 21114
              </p>
            </div>

            <p className="text-xl text-stone-700 leading-relaxed max-w-4xl mx-auto mb-16">
              Crofton Family Practice is located in Anne Arundel County, Maryland and has been a cornerstone of 
              community health since the late 1960s. It is currently owned and operated by Dr Allison N Williams 
              who purchased it in 2018. Since becoming an MDVIP affiliated practice it is now possible for all patients to enjoy a personalized experience in healthcare. Award Winning Care Delivered With Customer Satisfaction is Crofton Family Practice's Signature.
            </p>

            {/* Awards Display - Side by Side */}
            <div className="max-w-3xl mx-auto mt-12">
              <div className="flex justify-center gap-4 items-end">
                <div className="flex flex-col items-center">
                  <img
                    src="https://customer-assets.emergentagent.com/job_premium-med-care/artifacts/vb692rv2_Business%20Rate%20Award.png"
                    alt="Best of BusinessRate 2024 - Allison Williams, Family Practice Physician, Crofton"
                    className="w-40 h-auto rounded-lg shadow-2xl hover:scale-105 transition-transform duration-500 border-2 border-amber-200"
                  />
                </div>
                <div className="flex flex-col items-center">
                  <img
                    src="https://customer-assets.emergentagent.com/job_premium-med-care/artifacts/itjvrgkg_Awards%202.jpeg"
                    alt="Certificate of Excellence - Most Engaged New Practice, Aledade Accountable Care 103"
                    className="w-56 h-auto rounded-lg shadow-2xl hover:scale-105 transition-transform duration-500 border-2 border-amber-200"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Patients Are Saying Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-3">
              What Patients Are Saying
            </h2>
            <div className="h-1 w-24 bg-gradient-to-r from-amber-500 via-amber-500 to-orange-500 mx-auto mb-3"></div>
            <p className="text-base text-stone-600 max-w-3xl mx-auto">
              Real experiences from our valued patients
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Testimonial 1 - Kayla J */}
            <div className="bg-gradient-to-br from-stone-50 to-amber-50 rounded-xl p-6 shadow-lg border-2 border-stone-100 hover:shadow-xl transition-all duration-300">
              <div className="mb-4">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-amber-600">K</span>
                  </div>
                  <div className="ml-3">
                    <h3 className="font-bold text-stone-900 text-base">Kayla J</h3>
                    <div className="flex text-amber-500 text-sm">
                      ★★★★★
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-stone-700 leading-relaxed italic mb-3">
                "Dr. Williams and her team are fantastic, and I highly recommend them, especially for anyone dealing with more complicated health issues that are maybe not being fully or adequately addressed at other practices. Dr. Williams really spends the time to hear and review all your symptoms and her expertise and curiousity helped her quickly address health issues I spent more than 9 months and many tests investigating with other doctors and specialists with no success. I was so frustrated with my health and healthcare until I switched to this practice. Dr. Williams and the MDVIP program are worth the extra investment!"
              </p>
            </div>

            {/* Testimonial 2 - Anna K */}
            <div className="bg-gradient-to-br from-stone-50 to-amber-50 rounded-xl p-6 shadow-lg border-2 border-stone-100 hover:shadow-xl transition-all duration-300">
              <div className="mb-4">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-amber-600">A</span>
                  </div>
                  <div className="ml-3">
                    <h3 className="font-bold text-stone-900 text-base">Anna K</h3>
                    <div className="flex text-amber-500 text-sm">
                      ★★★★★
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-stone-700 leading-relaxed italic mb-3">
                "I've been seeing Dr. Williams for about 5 years at Crofton Family Practice. She has treated both acute and chronic issues with compassion, kindness, and thoroughness. You can tell she really cares about you, and takes her work as a doctor and healer seriously. Her staff is wonderful as well. I recommend her practice very highly."
              </p>
            </div>

            {/* Testimonial 3 - Wanda J */}
            <div className="bg-gradient-to-br from-stone-50 to-amber-50 rounded-xl p-6 shadow-lg border-2 border-stone-100 hover:shadow-xl transition-all duration-300">
              <div className="mb-4">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-amber-600">W</span>
                  </div>
                  <div className="ml-3">
                    <h3 className="font-bold text-stone-900 text-base">Wanda J</h3>
                    <div className="flex text-amber-500 text-sm">
                      ★★★★★
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-stone-700 leading-relaxed italic mb-3">
                "This is my doctor's practice, and I have been a patient here for years. She has an outstanding practice with numerous programs designed to assist with your overall health."
              </p>
            </div>

            {/* Testimonial 4 - BJ Murray - Centered on bottom row */}
            <div className="bg-gradient-to-br from-stone-50 to-amber-50 rounded-xl p-6 shadow-lg border-2 border-stone-100 hover:shadow-xl transition-all duration-300 lg:col-start-2">
              <div className="mb-4">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-amber-600">B</span>
                  </div>
                  <div className="ml-3">
                    <h3 className="font-bold text-stone-900 text-base">BJ Murray</h3>
                    <div className="flex text-amber-500 text-sm">
                      ★★★★★
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-stone-700 leading-relaxed italic mb-3">
                "Dr. Williams has been my doctor for many years. Her professionalism and thoughtful approach have always stood out. She listens carefully, explains things clearly, and always treats me with kindness and respect."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MDVIP CTA */}
      <MDVIPCallToAction />
    </AnimatedPage>
  );
};

export default OurPractice;
